import React from 'react';
const styles = require('./module.component.module.css');
import {
  CheckIcon,
  PreRevenueIcon,
  PreSeedIcon,
  RightArrowIcon,
  SeedBeyondIcon,
} from '@sketchnote/icons';
import classNames from 'classnames';

import { Button } from '../button/button.component';
import { Tag } from '../tags/tags.component';

export interface ModulePorps {
  title: string;
  tag?: string;
  coverImg?: string;
  description?: string;
  footerTxt?: string;
  onClick?: ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
  size?: 'xl' | 'lg' | 'md';
  hasStarted?: boolean;
  stage: 'pre-revenue' | 'pre-seed' | 'seed-beyond';
  className?: string;
  fullWidth?: boolean;
}

const Footer = ({ text, hasStarted, onClick }: any) => {
  return (
    <button
      onClick={onClick}
      className={classNames({
        'font-medium text-gray-600 rounded focus:outline-none inline-flex items-center mt-6 p-8 text-xs bg-gray-100':
          true,
        'bg-green-100 text-green-900': hasStarted,
      })}
      type="button"
    >
      <span className="mr-13 flex">
        <CheckIcon />
      </span>
      {text}
    </button>
  );
};

export const Stage = ({ type }: { type: 'pre-revenue' | 'pre-seed' | 'seed-beyond' }) => {
  if (type === 'pre-revenue') {
    return (
      <div className="inline-flex items-center">
        <span className="mr-6 flex">
          <PreRevenueIcon />
        </span>
        <span className="text-xss font-semibold">Pre-revenue</span>
      </div>
    );
  } else if (type === 'pre-seed') {
    return (
      <div className="inline-flex items-center">
        <span className="mr-6 flex">
          <PreSeedIcon />
        </span>
        <span className="text-xss font-semibold">Pre-seed to Seed</span>
      </div>
    );
  } else if (type === 'seed-beyond') {
    return (
      <div className="inline-flex items-center">
        <span className="mr-6 flex">
          <SeedBeyondIcon />
        </span>
        <span className="text-xss font-semibold">{'Seed & Beyond'}</span>
      </div>
    );
  } else {
    return null;
  }
};

export const Module = ({
  title,
  coverImg = 'https://via.placeholder.com/150',
  tag,
  description,
  footerTxt,
  size = 'md',
  hasStarted = false,
  onClick,
  stage,
  className,
  fullWidth = true,
}: ModulePorps) => {
  if (size === 'md') {
    return (
      <div
        className={classNames({
          [`border border-gray-200 cursor-pointer bg-white rounded`]: true,
          [className || '']: !!className,
          [styles['module-md']]: !fullWidth,
        })}
      >
        <div className="p-10 pb-0 border-b border-gray-200">
          <Tag className="bg-purple-600 z-10 absolute">{tag}</Tag>
          <div className="m-auto" style={{ width: '27.2rem', height: '14.4rem' }}>
            <img src={coverImg} className=" max-h-full max-w-full m-auto" alt="card image" />
          </div>
        </div>
        <div className="px-12 py-16">
          <h5 className="text-gray-999 font-bold mb-16">{title}</h5>
          <div className="text-gray-600 text-sm mb-16">{description}</div>
          <Stage type={stage} />
          <Footer hasStarted={hasStarted} text={footerTxt} onClick={onClick} />
        </div>
      </div>
    );
  } else if (size === 'lg') {
    return (
      <div
        className={classNames({
          [`border border-gray-200 cursor-pointer bg-white items-center rounded flex`]: true,
          [className || '']: !!className,
          [styles['module-md']]: !fullWidth,
        })}
      >
        <div className="flex flex-col h-full w-160 p-10 justify-center min-w-160">
          <img src={coverImg} alt="card image" />
        </div>
        <div className={`px-12 py-16 border-gray-200 border-l ${styles['module-content']}`}>
          <div className="flex justify-between">
            <Tag className="bg-purple-600 z-10 tag">{tag}</Tag>
            <Stage type={stage} />
          </div>

          <h5 className="mb-4 mt-20 font-bold">{title}</h5>
          <div className="text-gray-600 text-sm mb-8">{description}</div>

          <Footer hasStarted={hasStarted} text={footerTxt} onClick={onClick} />
        </div>
      </div>
    );
  } else if (size === 'xl') {
    return (
      <div
        className={`${styles['module-xl']} border border-gray-200 cursor-pointer bg-white rounded max-h-336`}
      >
        <div
          className={`border-r border-gray-200 rounded-tl rounded-bl w-6/12`}
          style={{ marginTop: '-0.5px' }}
        >
          <img src={coverImg} alt="card image" className="max-h-full max-w-full m-auto" />
        </div>
        <div className={`px-40 py-32 ${styles['module-content']}`}>
          <div className="flex justify-between">
            <Tag className="bg-purple-600 z-10 tag">{tag}</Tag>
            <Stage type={stage} />
          </div>

          <h3 className="mb-8 mt-30">{title}</h3>
          <div className="text-gray-600 text-sm mb-20">{description}</div>

          <Footer hasStarted={hasStarted} text={footerTxt} onClick={onClick} />
          <Button status="ghost" className="mt-26 pl-0" iconRight={<RightArrowIcon />}>
            Discover all features
          </Button>
        </div>
      </div>
    );
  }
  return null;
};
